import { ReactNode, useEffect, useRef, useState } from 'react';
import { Popover } from 'react-aria-components';
import styled from 'styled-components';
import { KIcon } from '@karnott/icons';
import { pixelSpacing, spacing } from '@karnott/theme';
import { PopupContent } from '../popup-content';
import { PopupHandler } from '../popup-handler';

type PopupProps = {
  title: string;
  showSelection: boolean;
  disabled: boolean;
  Icon?: KIcon;
  children: ReactNode;
};

export function Popup({ title, showSelection, disabled, Icon, children }: PopupProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return () => {};
    }

    const closeOnClickOutside = (e: MouseEvent) =>
      e.target instanceof Element &&
      !popoverRef.current?.contains(e.target) &&
      !triggerRef.current?.contains(e.target) &&
      setIsOpen(false);

    const closeOnTriggerScroll = (e: Event) =>
      e.target instanceof Element && triggerRef.current && e.target.contains(triggerRef.current) && setIsOpen(false);

    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('click', closeOnClickOutside, true);
    document.addEventListener('scroll', closeOnTriggerScroll, true);
    document.addEventListener('keydown', closeOnEscape, true);
    return () => {
      document.removeEventListener('click', closeOnClickOutside, true);
      document.removeEventListener('scroll', closeOnTriggerScroll, true);
      document.removeEventListener('keydown', closeOnEscape, true);
    };
  }, [isOpen]);

  return (
    <>
      <PopupHandler
        ref={triggerRef}
        id="area-handler"
        title={title}
        selectionLabel={title}
        hasSelection={showSelection}
        isOpen={isOpen}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        disabled={disabled}
        Icon={Icon}
      />
      <Popover
        ref={popoverRef}
        triggerRef={triggerRef}
        isOpen={isOpen}
        offset={spacing('xSmall')}
        placement="bottom start"
        isNonModal
        shouldCloseOnInteractOutside={() => false}
      >
        <Content>{children}</Content>
      </Popover>
    </>
  );
}

const Content = styled(PopupContent)`
  display: flex;
  gap: ${pixelSpacing('regular')};
  min-height: 50px;
  align-items: center;
  width: max-content;
`;
